import { useState, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrgStore } from '@/stores/orgStore'
import { usePublishPageData } from '@/stores/pageDataStore'
import { assetsApi } from '@/api/clients/assetsApi'
import { economicsApi } from '@/api/clients/economicsApi'
import { hndlApi } from '@/api/clients/hndlApi'
import { graphApi } from '@/api/clients/graphApi'
import type { AiAgentOutput, AiAnalysis, AssetOccurrence, HndlSimulateRequest } from '@/api/types'
import { PageHeader, Card, Button, Field, TextInput } from '@/components/common/ui'
import { BandBadge, Badge } from '@/components/common/Badge'
import { Spinner, QueryState } from '@/components/common/State'
import { MoneyValue } from '@/components/common/MoneyValue'
import { Markdown } from '@/components/common/Markdown'
import { gaugeColor } from '@/components/charts/RiskGauge'

// A standalone-source asset has no inventory; the backend serializes that as the
// all-zeros UUID (non-nullable column), which must be treated as "no inventory".
const NIL_UUID = '00000000-0000-0000-0000-000000000000'

type AssetTab = 'overview' | 'evidence' | 'simulate' | 'ai'
const ASSET_TABS: { key: AssetTab; label: string; icon: string }[] = [
  { key: 'overview', label: 'Overview', icon: 'insights' },
  { key: 'evidence', label: 'Evidence', icon: 'fact_check' },
  { key: 'simulate', label: 'Simulate', icon: 'science' },
  { key: 'ai', label: 'AI analysis', icon: 'psychology' },
]

export default function AssetDetails() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { currentOrgId: orgId } = useOrgStore()
  const [tab, setTab] = useState<AssetTab>('overview')

  const detail = useQuery({
    queryKey: ['asset', orgId, id],
    queryFn: () => assetsApi.get(orgId, id),
    enabled: !!orgId && !!id,
  })

  const a = detail.data
  const score = detail.data?.score

  // Publish a compact snapshot so the assistant can "explain this asset".
  usePublishPageData(
    useMemo(
      () => ({
        page: 'Asset detail',
        asset: a
          ? {
              id: a.id,
              name: a.name,
              algorithm: a.algorithm,
              quantum_risk: a.quantum_risk,
              band: score?.band,
              score: score?.score,
              hndl_window: score?.hndl_window,
              recommended_target: score?.recommended_target,
            }
          : { id },
      }),
      [a, score, id]
    )
  )

  return (
    <div>
      <PageHeader
        title={a?.name || 'Asset'}
        subtitle={
          a
            ? `${a.algorithm}${a.key_size ? ` ${a.key_size}` : ''} · ${a.asset_type}${
                a.inventory_name ? ` · ${a.inventory_name}` : ''
              }`
            : undefined
        }
        actions={
          <Button icon="arrow_back" onClick={() => navigate(-1)}>
            Back
          </Button>
        }
      />

      <QueryState isLoading={detail.isLoading} error={detail.error} isEmpty={!a && !detail.isLoading}
        emptyProps={{ icon: 'search_off', title: 'Asset not found' }}>
        {a && (
          <div className="space-y-5">
            {/* Summary header: score + migration status + actions */}
            <Card>
              <div className="flex flex-wrap items-center gap-4">
                {score ? (
                  <>
                    <BandBadge band={score.band} />
                    <span className="text-3xl font-bold text-text-light dark:text-text-dark">
                      {score.score?.toFixed(1)}
                    </span>
                    <span className="text-xs text-subtle-light dark:text-subtle-dark">
                      via {score.scorer} · {((score.confidence ?? 0) * 100).toFixed(0)}% confidence
                    </span>
                  </>
                ) : (
                  <span className="text-sm text-subtle-light dark:text-subtle-dark">
                    Not yet scored — scoring runs after ingest.
                  </span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <TransitionStatus orgId={orgId} assetId={a.id} state={a.transition_state} />
                  <Link to="/waivers">
                    <Button variant="ghost" icon="gavel">
                      Request waiver
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>

            {/* Tab bar */}
            <div className="flex gap-1 border-b border-border-light dark:border-border-dark overflow-x-auto">
              {ASSET_TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                    tab === t.key
                      ? 'border-primary text-primary dark:border-secondary dark:text-secondary'
                      : 'border-transparent text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark'
                  }`}
                >
                  <span className="material-icons text-base">{t.icon}</span>
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 'overview' && (
              <div className="space-y-5">
            {/* Facts */}
            <Card title="Asset facts">
              <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-3 text-sm">
                {/* Belongs to */}
                <div>
                  <dt className="label-sm text-subtle-light dark:text-subtle-dark">Inventory</dt>
                  <dd className="text-text-light dark:text-text-dark">
                    {a.inventory_id && a.inventory_id !== NIL_UUID ? (
                      <Link
                        to={`/inventories/${a.inventory_id}`}
                        className="text-primary dark:text-secondary hover:underline"
                      >
                        {a.inventory_name || 'View inventory'}
                      </Link>
                    ) : (
                      '— (standalone source)'
                    )}
                  </dd>
                </div>
                {a.source_name && <Fact label="Source" value={a.source_name} />}
                {a.business_unit && <Fact label="Business unit" value={a.business_unit} />}
                {a.criticality && <Fact label="Criticality" value={a.criticality} />}
                <Fact label="Algorithm" value={`${a.algorithm}${a.key_size ? ` ${a.key_size}` : ''}`} />
                <Fact label="Type" value={a.asset_type} />
                <Fact label="Quantum risk" value={a.quantum_risk} />
                <Fact label="Material state" value={a.material_state} />
                {score?.hndl_window != null && (
                  <Fact label="HNDL window" value={`${score.hndl_window.toFixed(1)} yrs`} />
                )}
                {score?.recommended_target && (
                  <Fact label="Recommended target" value={score.recommended_target} />
                )}
                <Fact label="External ref" value={a.external_ref || a.bom_ref || '—'} mono />
                {a.cert_issuer && <Fact label="Cert issuer" value={a.cert_issuer} />}
                {a.cert_subject && <Fact label="Cert subject" value={a.cert_subject} />}
                {a.cert_expiry && <Fact label="Cert expiry" value={a.cert_expiry.slice(0, 10)} />}
              </dl>
            </Card>

            {/* Factor breakdown */}
            {score?.factor_breakdown && Object.keys(score.factor_breakdown).length > 0 && (
              <Card title="Factor breakdown">
                <FactorBreakdown breakdown={score.factor_breakdown} />
              </Card>
            )}

            {/* How to fix */}
            {(score?.explanation || score?.recommendation || score?.recommended_target) && (
              <Card title="How to fix">
                {score?.recommended_target && (
                  <div className="mb-3 flex items-center gap-2 rounded-lg bg-primary/5 dark:bg-secondary/10 px-3 py-2">
                    <span className="material-icons text-base text-primary dark:text-secondary">
                      bolt
                    </span>
                    <span className="text-sm text-text-light dark:text-text-dark">
                      Migrate <span className="font-semibold">{a.algorithm}</span> →{' '}
                      <span className="font-semibold">{score.recommended_target}</span>
                    </span>
                  </div>
                )}
                {score?.recommendation && (
                  <Section title="Recommendation">{score.recommendation}</Section>
                )}
                {score?.explanation && <Section title="Why">{score.explanation}</Section>}
              </Card>
            )}
              </div>
            )}

            {tab === 'evidence' && (
              <div className="space-y-5">
                {/* Where it's used — discovery evidence */}
                <Occurrences occurrences={a.occurrences} />

                {/* Evidence trail */}
                {score?.applied_policies && score.applied_policies.length > 0 && (
                  <Card title="Applied policies (evidence trail)">
                    <ul className="space-y-1 text-xs font-mono">
                      {score.applied_policies.map((p, i) => (
                        <li
                          key={i}
                          className="p-2 rounded-lg bg-container-light dark:bg-container-dark text-text-light dark:text-text-dark"
                        >
                          {p.policy_id} · {p.rule_id} → {p.effect}
                        </li>
                      ))}
                    </ul>
                  </Card>
                )}

                {/* Blast radius */}
                <BlastRadius orgId={orgId} assetId={a.id} />
              </div>
            )}

            {tab === 'simulate' && (
              <div className="grid md:grid-cols-2 gap-5">
                <HndlSimulator orgId={orgId} assetId={a.id} />
                <RoiSimulator orgId={orgId} assetId={a.id} inventoryId={a.inventory_id} />
              </div>
            )}

            {tab === 'ai' && <AiAnalysisPanel orgId={orgId} assetId={a.id} />}
          </div>
        )}
      </QueryState>
    </div>
  )
}

// ── Migration (transition) status ─────────────────────────────────────────────

const MIGRATION_META: Record<string, { label: string; variant: 'neutral' | 'warning' | 'success' }> = {
  not_started: { label: 'Not migrated', variant: 'neutral' },
  in_progress: { label: 'Migrating', variant: 'warning' },
  completed: { label: 'Migrated', variant: 'success' },
}

function TransitionStatus({
  orgId,
  assetId,
  state,
}: {
  orgId: string
  assetId: string
  state?: { status?: string } | null
}) {
  const qc = useQueryClient()
  const current = state?.status && MIGRATION_META[state.status] ? state.status : 'not_started'
  const mut = useMutation({
    mutationFn: (status: string) =>
      graphApi.recordTransition(orgId, { asset_id: assetId, state: { status } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['asset', orgId, assetId] }),
  })
  const meta = MIGRATION_META[current]
  return (
    <div className="flex items-center gap-1.5">
      <Badge label={meta.label} variant={meta.variant} />
      <select
        value={current}
        disabled={mut.isPending}
        onChange={(e) => mut.mutate(e.target.value)}
        title="Set migration status"
        className="text-xs rounded-md border border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark px-2 py-1 text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/40"
      >
        <option value="not_started">Not migrated</option>
        <option value="in_progress">Migrating</option>
        <option value="completed">Migrated</option>
      </select>
    </div>
  )
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="label-sm text-subtle-light dark:text-subtle-dark">{label}</dt>
      <dd
        className={`text-text-light dark:text-text-dark ${mono ? 'font-mono text-xs break-all' : 'capitalize'}`}
      >
        {value || '—'}
      </dd>
    </div>
  )
}

// Occurrences — where discovery observed this crypto asset (file/host · line · context).
function Occurrences({ occurrences }: { occurrences?: AssetOccurrence[] }) {
  const list = Array.isArray(occurrences) ? occurrences : []
  const n = list.length
  return (
    <Card title={`Where it's used${n ? ` · ${n} location${n > 1 ? 's' : ''}` : ''}`}>
      {n === 0 ? (
        <p className="text-sm text-subtle-light dark:text-subtle-dark">
          No location evidence from discovery for this asset.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {list.map((o, i) => {
            const ctx = o.additionalContext ?? o.additional_context
            return (
              <li
                key={i}
                className="text-xs font-mono rounded-lg bg-container-light dark:bg-container-dark px-2.5 py-2 text-text-light dark:text-text-dark"
              >
                <span className="text-primary dark:text-secondary break-all">{o.location || '—'}</span>
                {o.line != null && (
                  <span className="text-subtle-light dark:text-subtle-dark">:{o.line}</span>
                )}
                {ctx && (
                  <span className="block mt-0.5 text-subtle-light dark:text-subtle-dark break-all">
                    {ctx}
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </Card>
  )
}

// ── Factor breakdown ──────────────────────────────────────────────────────────

const FACTOR_LABELS: Record<string, string> = {
  base_exposure: 'Base exposure',
  hndl_amplifier: 'HNDL urgency',
  quantum_exposure: 'Quantum exposure',
  data_sensitivity: 'Data sensitivity',
  business_criticality: 'Business criticality',
  regulatory_scope: 'Regulatory scope',
  business_factor: 'Business factor',
  physics_window: 'Physics (Mosca) window',
  deadline_window: 'Deadline window',
  deadline_drives: 'Deadline drives urgency',
}
const WINDOW_FACTORS = new Set(['physics_window', 'deadline_window'])

function factorLabel(k: string): string {
  return FACTOR_LABELS[k] ?? k.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}

// FactorBreakdown renders the [0,1] contribution factors as bars coloured by magnitude
// (higher = warmer), and the non-ratio factors (year windows, the deadline_drives flag)
// as plain labelled values — rendering those as % bars would be meaningless.
function FactorBreakdown({ breakdown }: { breakdown: Record<string, number> }) {
  const entries = Object.entries(breakdown)
  const isRatio = ([k, v]: [string, number]) =>
    !WINDOW_FACTORS.has(k) && k !== 'deadline_drives' && v >= 0 && v <= 1
  const ratios = entries.filter(isRatio)
  const windows = entries.filter(([k]) => WINDOW_FACTORS.has(k))
  const others = entries.filter(([k, v]) => !WINDOW_FACTORS.has(k) && k !== 'deadline_drives' && !(v >= 0 && v <= 1))
  const drives = breakdown['deadline_drives']

  return (
    <div className="space-y-2.5">
      <div className="space-y-2">
        {ratios.map(([k, v]) => {
          const pct = Math.min(100, Math.max(0, v * 100))
          return (
            <div key={k}>
              <div className="flex justify-between text-xs mb-0.5">
                <span className="text-text-light dark:text-text-dark">{factorLabel(k)}</span>
                <span className="text-subtle-light dark:text-subtle-dark">{pct.toFixed(0)}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-container-light dark:bg-container-dark overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${pct}%`, backgroundColor: gaugeColor(pct) }}
                />
              </div>
            </div>
          )
        })}
      </div>

      {(windows.length > 0 || drives != null || others.length > 0) && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 pt-2 text-xs text-subtle-light dark:text-subtle-dark border-t border-border-light dark:border-border-dark">
          {windows.map(([k, v]) => (
            <span key={k}>
              {factorLabel(k)}:{' '}
              <span className="text-text-light dark:text-text-dark font-medium">
                {v.toFixed(1)} yrs
              </span>
            </span>
          ))}
          {drives != null && (
            <span>
              Deadline drives urgency:{' '}
              <span className="text-text-light dark:text-text-dark font-medium">
                {drives ? 'yes' : 'no'}
              </span>
            </span>
          )}
          {others.map(([k, v]) => (
            <span key={k}>
              {factorLabel(k)}:{' '}
              <span className="text-text-light dark:text-text-dark font-medium">{v}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-3 last:mb-0">
      <p className="label-sm text-subtle-light dark:text-subtle-dark mb-1">{title}</p>
      <p className="text-sm text-text-light dark:text-text-dark whitespace-pre-wrap">{children}</p>
    </div>
  )
}

// ── AI analysis ───────────────────────────────────────────────────────────────

const AGENT_META: Record<string, { label: string; icon: string }> = {
  threat: { label: 'Threat', icon: 'gpp_maybe' },
  hndl: { label: 'Harvest-Now-Decrypt-Later', icon: 'lock_clock' },
  'business-impact': { label: 'Business Impact', icon: 'payments' },
  compliance: { label: 'Compliance', icon: 'verified_user' },
  control: { label: 'Controls', icon: 'tune' },
}

function VerdictBadge({ verdict }: { verdict: string }) {
  if (verdict === 'passed') return <Badge label="verified" variant="success" />
  if (verdict.startsWith('dropped')) return <Badge label="critic-flagged" variant="warning" />
  return null // advisor-only ("unverified") — no badge; section disclaimer covers trust
}

function AgentCard({ a }: { a: AiAgentOutput }) {
  const meta = AGENT_META[a.agent] ?? { label: a.agent, icon: 'smart_toy' }
  return (
    <details
      open={a.dropped}
      className="rounded-xl border border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark"
    >
      <summary className="flex items-center gap-2 px-4 py-3 cursor-pointer select-none list-none">
        <span className="material-icons text-lg text-primary dark:text-secondary">{meta.icon}</span>
        <span className="font-semibold text-text-light dark:text-text-dark">{meta.label}</span>
        <span className="text-[11px] text-subtle-light dark:text-subtle-dark font-mono">
          {a.prompt_version}
        </span>
        <span className="ml-auto flex items-center gap-2">
          <VerdictBadge verdict={a.verdict} />
          <span className="material-icons text-base text-subtle-light dark:text-subtle-dark">
            expand_more
          </span>
        </span>
      </summary>
      <div className="px-4 pb-4 pt-1 border-t border-border-light dark:border-border-dark">
        {a.dropped && (
          <p className="mb-2 text-xs text-yellow-700 dark:text-yellow-400">
            The critic flagged claims in this analysis ({a.verdict.replace('dropped_', '')}). Treat
            with caution — the deterministic score + evidence trail remain authoritative.
          </p>
        )}
        <Markdown>{a.text}</Markdown>
      </div>
    </details>
  )
}

function AiAnalysisPanel({ orgId, assetId }: { orgId: string; assetId: string }) {
  const [run, setRun] = useState(false)
  const q = useQuery<AiAnalysis>({
    queryKey: ['ai-analysis', orgId, assetId],
    queryFn: () => assetsApi.aiAnalysis(orgId, assetId) as Promise<AiAnalysis>,
    enabled: run && !!orgId,
    retry: false,
  })

  return (
    <Card title="AI analysis">
      {!run && (
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-subtle-light dark:text-subtle-dark">
            Grounded multi-agent enrichment (threat, HNDL, business impact, compliance, controls)
            with an executive bottom line. Advisory — the deterministic score stays authoritative.
          </p>
          <Button icon="psychology" variant="primary" onClick={() => setRun(true)}>
            Run AI analysis
          </Button>
        </div>
      )}

      {run && q.isLoading && <Spinner label="Running analysis… (up to ~40s; cached after)" />}

      {run && q.error && (
        <p className="text-sm text-subtle-light dark:text-subtle-dark">
          AI analysis unavailable (LLM off or degraded). The deterministic score above remains
          authoritative.
        </p>
      )}

      {run && q.data && !q.isLoading && (
        <div className="space-y-3">
          {q.data.ai_enabled === false ? (
            <p className="text-sm text-subtle-light dark:text-subtle-dark">
              {q.data.note || 'AI is off — the deterministic score + evidence trail are authoritative.'}
            </p>
          ) : (
            <>
              {/* Bottom line (synthesis) */}
              {q.data.synthesis && (
                <div className="rounded-xl border border-primary/30 dark:border-secondary/30 bg-primary/5 dark:bg-secondary/10 p-4">
                  <p className="label-sm uppercase tracking-label text-primary dark:text-secondary mb-1.5">
                    Bottom line
                  </p>
                  <Markdown>{q.data.synthesis}</Markdown>
                </div>
              )}

              {/* Specialist detail (collapsed by default) */}
              {q.data.agents.length > 0 && (
                <div className="space-y-2">
                  <p className="label-sm uppercase tracking-label text-subtle-light dark:text-subtle-dark">
                    Specialist detail
                  </p>
                  {q.data.agents.map((agent, i) => (
                    <AgentCard key={`${agent.agent}-${i}`} a={agent} />
                  ))}
                </div>
              )}

              <div className="flex items-center justify-between text-[11px] text-subtle-light dark:text-subtle-dark">
                <span>Advisory · deterministic score authoritative{q.data.cached ? ' · cached' : ''}</span>
                {typeof q.data.budget?.SpentUSD === 'number' && (
                  <span>AI spend this month: ${q.data.budget.SpentUSD.toFixed(2)}</span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  )
}

// ── Ported simulators / blast radius (were in AssetDrawer) ─────────────────────

function BlastRadius({ orgId, assetId }: { orgId: string; assetId: string }) {
  const graph = useQuery({
    queryKey: ['graph', orgId],
    queryFn: () => graphApi.graph(orgId),
    enabled: !!orgId,
    retry: false,
  })
  if (graph.isLoading || graph.error || !graph.data?.nodes?.length) return null

  const edges = graph.data.edges ?? []
  const neighborIds = new Set<string>()
  for (const e of edges) {
    if (e.source === assetId) neighborIds.add(e.target)
    if (e.target === assetId) neighborIds.add(e.source)
  }
  if (neighborIds.size === 0) return null
  const labelOf = (id: string) => graph.data!.nodes.find((n) => n.id === id)?.label ?? id

  return (
    <Card title={`Blast radius · ${neighborIds.size} connected`}>
      <ul className="flex flex-wrap gap-1">
        {[...neighborIds].slice(0, 40).map((id) => (
          <li
            key={id}
            className="px-2 py-0.5 text-xs rounded-md bg-container-light dark:bg-container-dark text-text-light dark:text-text-dark"
          >
            {labelOf(id)}
          </li>
        ))}
      </ul>
    </Card>
  )
}

function HndlSimulator({ orgId, assetId }: { orgId: string; assetId: string }) {
  const [params, setParams] = useState<Omit<HndlSimulateRequest, 'asset_id'>>({
    time_to_quantum_year: 2030,
    shelf_life_years: 10,
    migration_time_years: 2,
  })
  const sim = useMutation({
    mutationFn: () => hndlApi.simulate(orgId, { asset_id: assetId, ...params }),
  })
  // Live Mosca window (client-side, mirrors the backend scorer) so users see the
  // formula react as they edit the inputs.
  const thisYear = new Date().getFullYear()
  const moscaWindow =
    params.time_to_quantum_year - thisYear - (params.shelf_life_years + params.migration_time_years)
  const atRisk = moscaWindow <= 0

  return (
    <Card title="What-if: HNDL">
      <p className="text-xs text-subtle-light dark:text-subtle-dark mb-3">
        <span className="font-medium text-text-light dark:text-text-dark">
          Harvest-Now-Decrypt-Later
        </span>{' '}
        — an attacker records encrypted data today and decrypts it once a quantum computer (CRQC)
        can break this algorithm. Adjust the assumptions to see the asset's risk recompute.
      </p>
      <div className="grid grid-cols-3 gap-2">
        <Field label="Q-year (CRQC)">
          <TextInput
            type="number"
            value={params.time_to_quantum_year}
            onChange={(e) => setParams((p) => ({ ...p, time_to_quantum_year: Number(e.target.value) }))}
          />
          <p className="mt-1 text-[11px] text-subtle-light dark:text-subtle-dark">
            Year a quantum computer can break it
          </p>
        </Field>
        <Field label="Shelf life">
          <TextInput
            type="number"
            value={params.shelf_life_years}
            onChange={(e) => setParams((p) => ({ ...p, shelf_life_years: Number(e.target.value) }))}
          />
          <p className="mt-1 text-[11px] text-subtle-light dark:text-subtle-dark">
            Years data must stay secret
          </p>
        </Field>
        <Field label="Migration yrs">
          <TextInput
            type="number"
            value={params.migration_time_years}
            onChange={(e) => setParams((p) => ({ ...p, migration_time_years: Number(e.target.value) }))}
          />
          <p className="mt-1 text-[11px] text-subtle-light dark:text-subtle-dark">
            Years to move it to PQC
          </p>
        </Field>
      </div>

      {/* Live Mosca window from the current inputs */}
      <div className="mt-3 flex items-center gap-2 text-sm">
        <span className="text-subtle-light dark:text-subtle-dark">Mosca window</span>
        <span className={`font-semibold ${atRisk ? 'text-error' : 'text-text-light dark:text-text-dark'}`}>
          {moscaWindow.toFixed(0)} yr{Math.abs(moscaWindow) === 1 ? '' : 's'}
        </span>
        <span className={`text-xs ${atRisk ? 'text-error' : 'text-subtle-light dark:text-subtle-dark'}`}>
          {atRisk ? '· already harvestable — migrate now' : '· margin before exposure'}
        </span>
      </div>

      <details className="mt-2 text-xs text-subtle-light dark:text-subtle-dark">
        <summary className="cursor-pointer select-none hover:text-text-light dark:hover:text-text-dark">
          How it's calculated
        </summary>
        <div className="mt-2 space-y-1.5">
          <p>
            <span className="font-mono text-text-light dark:text-text-dark">
              Mosca window = Q-year − this year − (shelf life + migration years)
            </span>
          </p>
          <p>
            If the window is ≤ 0, data harvested today can be decrypted before you finish migrating —
            i.e. you are <span className="text-error">already at risk</span>. The shorter the window,
            the more it amplifies the asset's quantum-exposure score.
          </p>
          <p>
            <span className="font-medium">Simulate</span> re-runs the deterministic scorer with these
            assumptions and shows the score/band before → after. Nothing is saved.
          </p>
        </div>
      </details>

      <Button className="mt-3" icon="science" disabled={sim.isPending} onClick={() => sim.mutate()}>
        Simulate
      </Button>
      {sim.data && (
        <div className="mt-3 flex items-center gap-3 text-sm text-text-light dark:text-text-dark">
          <span className="flex items-center gap-1">
            <BandBadge band={sim.data.before.band} /> {sim.data.before.score.toFixed(1)}
          </span>
          <span className="material-icons text-base text-subtle-light dark:text-subtle-dark">
            arrow_forward
          </span>
          <span className="flex items-center gap-1">
            <BandBadge band={sim.data.after.band} /> {sim.data.after.score.toFixed(1)}
          </span>
        </div>
      )}
    </Card>
  )
}

function RoiSimulator({
  orgId,
  assetId,
  inventoryId,
}: {
  orgId: string
  assetId: string
  inventoryId?: string | null
}) {
  const sim = useMutation({ mutationFn: () => economicsApi.simulateAssetRoi(orgId, assetId) })
  const d = sim.data
  const noCost = !!d && d.cost_of_fix === 0
  return (
    <Card title="Remediation ROI">
      <p className="text-xs text-subtle-light dark:text-subtle-dark mb-3">
        Is migrating this asset to post-quantum crypto worth it? Compares the{' '}
        <span className="font-medium text-text-light dark:text-text-dark">$-at-risk you avoid</span>{' '}
        against the <span className="font-medium text-text-light dark:text-text-dark">cost to fix</span> it.
      </p>
      <Button icon="calculate" disabled={sim.isPending} onClick={() => sim.mutate()}>
        Simulate
      </Button>
      {d && (
        <>
          <div className="grid grid-cols-3 gap-2 mt-3 text-sm text-text-light dark:text-text-dark">
            <div>
              <p className="text-xs text-subtle-light dark:text-subtle-dark">Avoided</p>
              <MoneyValue
                value={d.delta_at_risk_avoided}
                currency={d.currency}
                className="font-semibold text-success"
              />
            </div>
            <div>
              <p className="text-xs text-subtle-light dark:text-subtle-dark">Cost of fix</p>
              <MoneyValue value={d.cost_of_fix} currency={d.currency} />
              <p className="mt-0.5 text-[11px] text-subtle-light dark:text-subtle-dark">
                <MoneyValue value={d.migration_cost} currency={d.currency} /> migration +{' '}
                <MoneyValue value={d.remediation_labor_cost} currency={d.currency} /> labour
              </p>
            </div>
            <div>
              <p className="text-xs text-subtle-light dark:text-subtle-dark">ROI</p>
              {noCost ? (
                <span className="text-subtle-light dark:text-subtle-dark">n/a</span>
              ) : (
                <span className={`font-semibold ${d.roi >= 0 ? 'text-success' : 'text-error'}`}>
                  {d.roi.toFixed(2)}×
                </span>
              )}
            </div>
          </div>

          {noCost ? (
            <div className="mt-2 rounded-lg bg-warning/10 px-3 py-2 text-xs text-text-light dark:text-text-dark">
              Migration cost &amp; labour aren&apos;t set for this inventory, so ROI &amp; payback
              can&apos;t be computed (only the avoided $-at-risk is real).
              {inventoryId && (
                <Link
                  to={`/inventories/${inventoryId}`}
                  className="ml-1 font-medium text-primary dark:text-secondary hover:underline"
                >
                  Set economics →
                </Link>
              )}
            </div>
          ) : (
            <p className="mt-2 text-xs text-subtle-light dark:text-subtle-dark">
              Net benefit{' '}
              <MoneyValue
                value={d.net_benefit}
                currency={d.currency}
                className="text-text-light dark:text-text-dark"
              />
              {d.payback_years != null && <> · Payback in {d.payback_years.toFixed(1)} yrs</>}
            </p>
          )}
        </>
      )}

      <details className="mt-2 text-xs text-subtle-light dark:text-subtle-dark">
        <summary className="cursor-pointer select-none hover:text-text-light dark:hover:text-text-dark">
          How it's calculated
        </summary>
        <div className="mt-2 space-y-1.5">
          <p>
            <span className="font-mono text-text-light dark:text-text-dark">
              ROI = (avoided − cost of fix) ÷ cost of fix
            </span>
          </p>
          <ul className="list-disc pl-4 space-y-0.5">
            <li>
              <span className="font-medium">Avoided</span> = the asset's current $-at-risk minus what
              remains after migration. The $-at-risk comes from this inventory's configured model —
              revenue / downtime / breach / regulatory exposure × quantum-exposure.
            </li>
            <li>
              <span className="font-medium">Cost of fix</span> = migration cost + remediation labour
              (effort-days × FTE daily rate), both set per inventory under Economics.
            </li>
            <li>
              <span className="font-medium">ROI ≥ 0</span> means fixing it pays for itself; higher is
              better. Payback = cost ÷ avoided-per-year.
            </li>
          </ul>
          <p>Figures are configured ground truth (§12.1) — never AI-estimated.</p>
        </div>
      </details>
    </Card>
  )
}
